import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const LocationContext = createContext(null);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocation must be used within a LocationProvider');
  return context;
};

const STORAGE_KEY = 'eventgoers_location';

export const LocationProvider = ({ children }) => {
  const [currentCity, setCurrentCity] = useState(null);       // { city, state, display, slug }
  const [currentNeighborhood, setCurrentNeighborhood] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);      // neighborhoods in current city
  const [allNeighborhoods, setAllNeighborhoods] = useState([]); // legacy support
  const [userCoords, setUserCoords] = useState(null);
  const [locationDetected, setLocationDetected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInSupportedArea, setIsInSupportedArea] = useState(true);

  // Fetch available cities on mount
  useEffect(() => {
    fetchCities();
    loadSavedLocation();
    fetchLegacyNeighborhoods();
  }, []);

  // When city changes, fetch its neighborhoods
  useEffect(() => {
    if (currentCity?.city) fetchCityNeighborhoods(currentCity.city);
  }, [currentCity?.city]);

  const fetchCities = async () => {
    try {
      const res = await api.get('/discover/cities');
      setAvailableCities(res.data.cities || []);
    } catch {}
  };

  const fetchLegacyNeighborhoods = async () => {
    try {
      const res = await api.get('/neighborhoods');
      setAllNeighborhoods(res.data.neighborhoods || []);
    } catch {}
  };

  const fetchCityNeighborhoods = async (city) => {
    try {
      const res = await api.get(`/discover/neighborhoods/${city}`);
      setNeighborhoods(res.data.neighborhoods || []);
    } catch { setNeighborhoods([]); }
  };

  const loadSavedLocation = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCurrentCity(parsed.city || null);
        setCurrentNeighborhood(parsed.neighborhood || null);
        setLocationDetected(true);
        setIsLoading(false);
        return;
      } catch {}
    }
    detectLocation();
  };

  const detectLocation = useCallback(async () => {
    setIsLoading(true);

    if (!navigator.geolocation) {
      setDefaultCity();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserCoords({ lat: latitude, lng: longitude });

        // Reverse geocode to get city
        try {
          const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g&result_type=locality`);
          const data = await res.json();
          if (data.results?.[0]) {
            const components = data.results[0].address_components;
            const city = components.find(c => c.types.includes('locality'))?.long_name || 'Denver';
            const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || 'CO';
            const neighborhood = components.find(c => c.types.includes('neighborhood'))?.long_name || '';

            const cityObj = { city, state, display: `${city}, ${state}`, slug: city.toLowerCase().replace(/\s+/g, '-') };
            selectCity(cityObj);

            if (neighborhood) {
              setCurrentNeighborhood({ name: neighborhood, slug: neighborhood.toLowerCase().replace(/\s+/g, '-') });
            }
            setIsInSupportedArea(true);
          } else {
            setDefaultCity();
          }
        } catch {
          setDefaultCity();
        }

        setLocationDetected(true);
        setIsLoading(false);
      },
      () => { setDefaultCity(); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  const setDefaultCity = () => {
    const defaultCity = { city: 'Denver', state: 'CO', display: 'Denver, CO', slug: 'denver' };
    selectCity(defaultCity);
    setLocationDetected(true);
    setIsLoading(false);
  };

  const selectCity = useCallback((cityObj) => {
    setCurrentCity(cityObj);
    setCurrentNeighborhood(null);
    setIsInSupportedArea(true);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ city: cityObj, neighborhood: null }));
  }, []);

  const selectNeighborhood = useCallback((neighborhood) => {
    setCurrentNeighborhood(neighborhood);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ city: currentCity, neighborhood }));
  }, [currentCity]);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentCity(null);
    setCurrentNeighborhood(null);
    setLocationDetected(false);
  }, []);

  // Legacy compatibility
  const nearbyNeighborhoods = neighborhoods;

  const value = {
    // New city-first API
    currentCity,
    availableCities,
    neighborhoods,
    selectCity,
    // Legacy compatibility
    currentNeighborhood,
    allNeighborhoods,
    nearbyNeighborhoods,
    locationDetected,
    isLoading,
    isInSupportedArea,
    userCoords,
    detectLocation,
    selectNeighborhood,
    clearLocation,
    fetchNeighborhoods: fetchLegacyNeighborhoods,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export default LocationContext;
